// ===== RTK QUERY BASE API CONFIGURATION =====
// src/store/api/baseApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { APP_CONFIG } from "../../constants/app.js";

/**
 * Base query configuration with authentication and error handling
 */
const baseQuery = fetchBaseQuery({
  baseUrl: APP_CONFIG.API_BASE_URL,
  timeout: APP_CONFIG.API_TIMEOUT,

  // Prepare headers for all requests
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = localStorage.getItem(APP_CONFIG.TOKEN_KEY);

    // Add authentication token if available
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    // Set content type for most requests
    if (!headers.get("content-type")) {
      headers.set("content-type", "application/json");
    }

    // Add custom headers
    headers.set("x-app-version", APP_CONFIG.APP_VERSION);
    headers.set("x-client-type", "web");

    // Add request ID for tracking
    headers.set("x-request-id", generateRequestId());

    return headers;
  },

  // Handle credentials
  credentials: "include",

  // Request interceptor for logging
  ...(APP_CONFIG.IS_DEVELOPMENT && {
    fetchFn: async (input, init) => {
      console.log(`🚀 API Request: ${init?.method || "GET"} ${input}`);
      const response = await fetch(input, init);
      console.log(`✅ API Response: ${response.status} ${input}`);
      return response;
    },
  }),
});

/**
 * Enhanced base query with automatic token refresh and error handling
 */
const baseQueryWithReauth = async (args, api, extraOptions) => {
  // Execute the initial request
  let result = await baseQuery(args, api, extraOptions);

  // Handle different error scenarios
  if (result?.error) {
    const { status, data } = result.error;

    switch (status) {
      case 401:
        // Unauthorized - attempt token refresh
        console.log("🔄 Token expired, attempting refresh...");

        const refreshToken = localStorage.getItem(APP_CONFIG.REFRESH_TOKEN_KEY);
        if (refreshToken) {
          const refreshResult = await baseQuery(
            {
              url: "/api/auth/refresh",
              method: "POST",
              body: { refreshToken },
            },
            api,
            extraOptions
          );

          if (refreshResult?.data?.token) {
            // Store new tokens
            localStorage.setItem(
              APP_CONFIG.TOKEN_KEY,
              refreshResult.data.token
            );
            if (refreshResult.data.refreshToken) {
              localStorage.setItem(
                APP_CONFIG.REFRESH_TOKEN_KEY,
                refreshResult.data.refreshToken
              );
            }

            console.log("✅ Token refreshed successfully");

            // Retry original request with new token
            result = await baseQuery(args, api, extraOptions);
          } else {
            // Refresh failed - force logout
            console.log("❌ Token refresh failed, logging out...");
            handleAuthFailure();
          }
        } else {
          // No refresh token available
          handleAuthFailure();
        }
        break;

      case 403:
        // Forbidden - insufficient permissions
        console.warn(
          "🚫 Access denied:",
          data?.message || "Insufficient permissions"
        );

        // Dispatch notification about permission error
        api.dispatch({
          type: "ui/addNotification",
          payload: {
            type: "error",
            message:
              data?.message || "You do not have permission for this action",
          },
        });
        break;

      case 404:
        // Not found
        console.warn("📄 Resource not found:", args);
        break;

      case 422:
        // Validation error
        console.warn("📋 Validation error:", data);
        break;

      case 429:
        // Rate limited
        console.warn("⏰ Rate limited, please try again later");

        api.dispatch({
          type: "ui/addNotification",
          payload: {
            type: "warning",
            message: "Too many requests. Please wait and try again.",
          },
        });
        break;

      case 500:
      case 502:
      case 503:
      case 504:
        // Server errors
        console.error("🔥 Server error:", status, data);

        api.dispatch({
          type: "ui/addNotification",
          payload: {
            type: "error",
            message: "Server error. Please try again later.",
          },
        });
        break;

      default:
        console.error("❌ API Error:", status, data);
    }
  }

  return result;
};

/**
 * Handle authentication failure
 */
const handleAuthFailure = () => {
  // Clear stored authentication data
  localStorage.removeItem(APP_CONFIG.TOKEN_KEY);
  localStorage.removeItem(APP_CONFIG.REFRESH_TOKEN_KEY);
  localStorage.removeItem(APP_CONFIG.USER_KEY);

  // Redirect to login page
  window.location.href = "/login";
};

/**
 * Generate unique request ID for tracking
 */
const generateRequestId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Base API configuration
 */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,

  // Tag types for cache invalidation
  tagTypes: [
    "User",
    "Dashboard",
    "Activity",
    "Report",
    "Settings",
    "Notification",
    "File",
    "Role",
    "Permission",
    "System",
  ],

  // Global configuration
  keepUnusedDataFor: APP_CONFIG.DEFAULT_CACHE_TTL,
  refetchOnMountOrArgChange: 30, // Refetch if data is older than 30 seconds
  refetchOnFocus: true,
  refetchOnReconnect: true,

  // Define endpoints (will be injected by individual API slices)
  endpoints: () => ({}),

  // Transform responses globally
  transformResponse: (response, meta, arg) => {
    // Log successful responses in development
    if (APP_CONFIG.IS_DEVELOPMENT) {
      console.log("📥 API Response Data:", response);
    }

    // Handle different response formats
    if (response && typeof response === "object") {
      // If response has data property, return it
      if ("data" in response) {
        return response;
      }

      // If response is array, wrap it
      if (Array.isArray(response)) {
        return { data: response };
      }
    }

    return response;
  },

  // Transform error responses
  transformErrorResponse: (response, meta, arg) => {
    console.error("🔥 API Error Response:", response);

    // Standardize error format
    return {
      status: response.status,
      message:
        response.data?.message || response.data?.error || "An error occurred",
      code: response.data?.code,
      details: response.data?.details,
      timestamp: new Date().toISOString(),
    };
  },
});

/**
 * Utility function to create standardized query configurations
 */
export const createQueryConfig = (options = {}) => {
  return {
    keepUnusedDataFor: options.cacheTTL || APP_CONFIG.DEFAULT_CACHE_TTL,
    refetchOnMountOrArgChange: options.refetchOnMount || 30,
    refetchOnFocus: options.refetchOnFocus ?? true,
    refetchOnReconnect: options.refetchOnReconnect ?? true,
    ...(options.polling && {
      pollingInterval:
        options.polling.interval || APP_CONFIG.DEFAULT_POLLING_INTERVAL,
    }),
    ...options,
  };
};

/**
 * Utility function to create standardized mutation configurations
 */
export const createMutationConfig = (options = {}) => {
  return {
    // Optimistic updates configuration
    ...(options.optimistic && {
      onQueryStarted: async (arg, { dispatch, queryFulfilled, ...api }) => {
        try {
          await queryFulfilled;

          // Success notification
          if (options.successMessage) {
            dispatch({
              type: "ui/addNotification",
              payload: {
                type: "success",
                message: options.successMessage,
              },
            });
          }
        } catch (error) {
          // Error notification
          if (options.errorMessage) {
            dispatch({
              type: "ui/addNotification",
              payload: {
                type: "error",
                message: options.errorMessage,
              },
            });
          }

          // Handle optimistic rollback if configured
          if (options.onOptimisticError) {
            options.onOptimisticError(error, api);
          }
        }
      },
    }),

    // Cache invalidation
    invalidatesTags: options.invalidatesTags || [],

    ...options,
  };
};

/**
 * Create enhanced hooks with additional functionality
 */
export const createEnhancedQuery = (queryFn, options = {}) => {
  return (args, queryOptions = {}) => {
    const result = queryFn(args, {
      ...createQueryConfig(options),
      ...queryOptions,
    });

    return {
      ...result,
      // Additional helper methods
      isFirstLoad: result.isLoading && !result.data,
      isRefreshing: result.isFetching && !!result.data,
      hasError: !!result.error,
      isEmpty:
        !result.isLoading &&
        (!result.data ||
          (Array.isArray(result.data) && result.data.length === 0)),
    };
  };
};

/**
 * Performance monitoring for API calls
 */
if (APP_CONFIG.PERFORMANCE_MONITORING) {
  // Add performance tracking
  const originalBaseQuery = baseQuery;

  const performanceBaseQuery = async (args, api, extraOptions) => {
    const startTime = performance.now();
    const result = await originalBaseQuery(args, api, extraOptions);
    const duration = performance.now() - startTime;

    // Log slow requests
    if (duration > 1000) {
      console.warn(`⚠️ Slow API call (${duration.toFixed(2)}ms):`, args);
    }

    // Track in analytics if available
    if (window.serviceAnalytics) {
      const endpoint = typeof args === "string" ? args : args.url;
      window.serviceAnalytics.trackPerformance(
        "API",
        endpoint,
        duration,
        result.error ? false : true
      );
    }

    return result;
  };
}

/**
 * Development utilities
 */
if (APP_CONFIG.IS_DEVELOPMENT) {
  // Make baseApi available globally for debugging
  window.baseApi = baseApi;

  // Add debugging utilities
  window.apiDebug = {
    clearCache: () => {
      console.log("🗑️ Clearing API cache...");
      return baseApi.util.resetApiState();
    },

    getQueries: () => {
      return baseApi.util.selectInvalidatedBy(
        { type: "User" } // Example - get all User queries
      );
    },

    invalidateAll: () => {
      console.log("♻️ Invalidating all cached data...");
      return baseApi.util.invalidateTags([
        "User",
        "Dashboard",
        "Report",
        "Settings",
        "Notification",
      ]);
    },
  };

  console.log("🔧 API Debug utilities available at window.apiDebug");
}

// Export the configured API
export default baseApi;
